import { ShopOriginConfiguration } from "src/model/shop.model";
import { APP_DEFAULT_LANGUAGE, ZARA_COOKIES_KEYS } from "./constants";
import { getEnv } from "./EnvironmentTools";
import { StoreConstants } from "./store-hooks";
import { StoreService } from "./store-hooks/core/StoreService";
import { ORIGIN_PARAM_NAME } from "src/constants/UrlConstants";

export const isLocal = window.location.hostname === "localhost";

export const PREOWNED_PATH = "preowned-resell"

export const getInitialBasePath = () => {
  const currentBasePath = getCurrentBasePath();
  const initiaBasePath = currentBasePath.replace(
    getMarketLanguage(),
    getCountryLanguage(),
  );
  return initiaBasePath;
};

export const USA_BASEPATH_VARIANTS = {
  EN: "/us/en/",
  ES: "/us/es/"
}

export const getInitialBasePathWithMarketIso = () => {
  const currentBasePath = getCurrentBasePath();
  const initiaBasePath = currentBasePath.replace(
    getCountryLanguage(),
    getMarketLanguage(),
  );
  return initiaBasePath;
};

export const getCurrentCountryBasePath = () => {
  return `${getCountryLanguage()}${PREOWNED_PATH}`
}

export const getCurrentBasePath = () => {
  const prefix = location
    ? location.pathname.match(/^\/.*\/.*\/preowned-resell/gm)
    : "";
  const hasLanguage = !!prefix;
  return hasLanguage ? prefix[0] : "/preowned-resell";
};

// El ISO de United Kingdom viaja como "GB" fuera de preowned, pero la URL de preowned siempre usa "uk".
export const toPreownedCountryIso = (iso: string): string => {
  const lowerCaseIso = iso?.toLowerCase();
  return lowerCaseIso === "gb" ? "uk" : lowerCaseIso;
};

export const checkURLIfGB = (): boolean => {
  const originRegexdata = location?.pathname?.match(/^\/([^/]+)/);
  if (originRegexdata?.length > 0 && originRegexdata[1]?.toLowerCase() === "gb") {
    const updatedUrl = location.href.replace(/\/gb(?=\/|$)/i, `/${toPreownedCountryIso("gb")}`);
    window.location.replace(updatedUrl);
    return true;
  }
  return false;
}

export const getEntryISOPathExtraction = (): string => {
  const originRegexdata = location?.pathname?.match(/^\/([^/]+)/);
  return originRegexdata?.length > 0 ? originRegexdata[1] : null;
};

export const getOriginSession = (): string => {
  const urlParams = new URLSearchParams(window.location.search);

  if (urlParams.has(ORIGIN_PARAM_NAME)) {
    return urlParams.get(ORIGIN_PARAM_NAME).toUpperCase();
  }
  return null;
};

export const hasUrlParam = (param: string): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has(param);
};

export const getCountryLanguage = (): string => {
  const configuration: ShopOriginConfiguration | any = StoreService.getObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
  );
  return `/${configuration?.country}/${
    configuration?.lang ?? APP_DEFAULT_LANGUAGE.toLowerCase()
  }/`;
};

export const getEntryISOLanguagePath = (): string => {
  const configuration: ShopOriginConfiguration | any = StoreService.getObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
  );
  return `/${configuration?.entryISO}/${configuration?.entryLang}/`;
};

export const getMarketLanguage = (): string => {
  const configuration: ShopOriginConfiguration | any = StoreService.getObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
  );
  return `/${configuration?.market}/${
    configuration?.lang ?? APP_DEFAULT_LANGUAGE.toLowerCase()
  }/`;
};

export const getCurrentLanguage = (): string => {
  const configuration: ShopOriginConfiguration | any = StoreService.getObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
  );
  return configuration?.lang ?? APP_DEFAULT_LANGUAGE.toLowerCase();
};

export const getCurrentCountry = (): string => {
  const configuration: ShopOriginConfiguration | any = StoreService.getObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
  );
  return configuration?.country ?? null;
};

export const getStoreId = (): number => {
  const configuration: ShopOriginConfiguration = StoreService.getObject(
    StoreConstants.SHOP_ORIGIN_CONFIGURATION,
  ) as ShopOriginConfiguration;
  return Number(configuration?.storeId) ?? null;
};

export const COUNTRIES = {
  fr: "France",
  es: "España",
  uk: "United Kingdom",
  us: "USA",
  de: "Deucheland",
};

export const ISOCODEHandler = (ISO: string) => {
  if (ISO?.toLowerCase() === "uk") return "GB";
  else return ISO?.toUpperCase();
};

export const getApiResellHost = (): string => {
  return getEnv()?.resellApiHost ?? "";
};

export const setZaraApiHost = (url:string): string => {
  return isLocal ? url : `${getEnv().zaraHost}${url}`;
}

export const setResellOShiftHost = (url:string): string => {
  return isLocal ? url : `${getEnv().resellOShiftHost}${url}`;
}

export const getCurrentDomainForCookies = () => {
  return `.${getEnv()?.domain}`
}

export const refreshUrlParams = (urlParams: URLSearchParams) => {
  const currentUrl = new URL(window.location.href);
  currentUrl.search = urlParams.toString();
  window.history.replaceState({}, "", currentUrl.toString());
}

export const getLanguageWithCountry = (): string => {
  const currentLanguage = getCurrentLanguage(); 
  const currentCountry = getCurrentCountry(); 
  const isoCountry = ISOCODEHandler(currentCountry); 
  return `${currentLanguage}-${isoCountry}` || "es-ES";
};

export const getPathFragment = (url:string) => {
  const regex = /^\/[^/]+\/[^/]+\/preowned-resell(\/.*)?$/; 
  const match = url.match(regex);

  return  match ? `${match[1] || ''}` : '';
}